import React, { useState, useEffect } from 'react';
import Dropdown from '../../Components/utils/ReuseDropdown';
import { FaEye, FaFilter, FaInfoCircle } from "react-icons/fa";
import axios from 'axios';
import { Link } from "react-router-dom";
import { IoIosCloseCircleOutline } from 'react-icons/io';
import { today } from '../../data/constants';
import { CSVLink } from 'react-csv';
import SearchInput from '../../Components/utils/ReuseSearchInput';
import Loader from '../../Components/utils/Loader';

function AdminOrders({ pre }) {
    const [orders, setOrders] = useState([]);
    const [filterType, setFilterType] = useState('All');
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [showOrderDetailsModel, setShowOrderDetailsModel] = useState(false);
    const [openedOrder, setOpenedOrder] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState("ID");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [showFiltersPopup, setShowFiltersPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tempFilterType, setTempFilterType] = useState(filterType);
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);

    const productFilters = ["All", "Active", "Out for Delivery", "Delivered", "Ready for Pickup", "Picked Up", "Completed", "Cancelled", "OnHold", "InDispute", "Resolved", "Past Due"];

    useEffect(() => {
        if (showFiltersPopup) {
            setTempFilterType(filterType);
            setTempStartDate(startDate);
            setTempEndDate(endDate);
        }
    }, [endDate, filterType, showFiltersPopup, startDate]);

    useEffect(() => {
        const fetchOrders = async () => {
            setIsLoading(true);
            const token = localStorage.getItem('adminToken');
            try {
                const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/orders/all/`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { pre }
                });
                if (response.data.success) {
                    setOrders(response.data.orders);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching orders:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchOrders();
    }, [pre]);

    useEffect(() => {
        if (filterType === 'All') {
            setFilteredOrders(orders);
        } else {
            const filtered = orders.filter(order => order.currentStatus === filterType);
            setFilteredOrders(filtered);
        }
    }, [filterType, orders]);

    const getCompletionDate = (statusArray) => {
        const completedStatus = statusArray?.find(status => status.name === "Completed");
        return completedStatus ? new Date(completedStatus.createdAt) : null;
    };

    const finalOrders = filteredOrders.filter(order => {
        const status = order.currentStatus;
        const completionDate = getCompletionDate(order.statusHistory);
        const query = searchQuery.toLowerCase();

        if (filterType !== "All" && status !== filterType) return false;

        if (startDate && endDate && completionDate) {
            const start = new Date(startDate).getTime();
            const end = new Date(endDate).getTime();
            const completionTime = completionDate.getTime();
            if (completionTime < start || completionTime > end) return false;
        } else if (startDate && endDate && !completionDate) {
            return false;
        }

        if (searchQuery) {
            if (searchType === "ID" && !order?.customOrderId?.toLowerCase().includes(query)) return false;
            if (searchType === "Buyer" && !order?.buyerId?.username?.toLowerCase().includes(query)) return false;
            if (searchType === "Seller") {
                const sellerSearchText = (
                    order?.sellerId?.companyName ||
                    order?.sellerId?.displayName ||
                    order?.sellerId?.userId?.username ||
                    ''
                ).toLowerCase();
                if (!sellerSearchText.includes(query)) return false;
            }
        }

        return true;
    });

    const handleOpenOrderDetails = (order) => {
        setOpenedOrder(order);
        setShowOrderDetailsModel(true);
    };

    const getSellerDisplayName = (order) => {
        if (order?.sellerModel === 'B2BSellerProfile' || order?.isB2B) {
            return order?.sellerId?.companyName || order?.sellerId?.displayName || order?.sellerId?.userId?.username || 'B2B Seller';
        }
        return order?.sellerId?.userId?.username || order?.sellerId?.displayName || 'Seller';
    };

    const getSellerLinkId = (order) => {
        if (order?.sellerModel === 'B2BSellerProfile' || order?.isB2B) {
            return order?.sellerId?.sellerId || null;
        }
        return order?.sellerId?._id || null;
    };

    const csvHeaders = [
        { label: "Order ID", key: "orderId" },
        { label: "Product ID", key: "productId" },
        { label: "Product Name", key: "productName" },
        { label: "Quantity", key: "quantity" },
        { label: "Buyer", key: "buyer" },
        { label: "Seller", key: "seller" },
        { label: "Sales Price", key: "salesPrice" },
        { label: "Delivery Fee", key: "deliveryFee" },
        { label: "Service Fee", key: "serviceFee" },
        { label: "Subtotal", key: "subtotal" },
        { label: "Tax", key: "tax" },
        { label: "Total Paid by Buyer", key: "totalPaid" },
        { label: "Status", key: "status" },
        { label: "Order Date", key: "orderDate" },
        { label: "Completion Date", key: "completionDate" },
        { label: "Payment Method", key: "paymentMethod" },
        { label: "Buyer Name", key: "buyerName" },
        { label: "Buyer Email", key: "buyerEmail" },
        { label: "Buyer Country", key: "buyerCountry" },
        { label: "Buyer City", key: "buyerCity" },
        { label: "Buyer Phone", key: "buyerPhone" },
        { label: "Seller Company", key: "sellerCompany" },
        { label: "B2B Listing Mode", key: "b2bListingMode" },
        { label: "B2B MOQ", key: "b2bMoq" },
    ];

    const csvData = finalOrders.map(order => ({
        orderId: order?.customOrderId ? `#${order.customOrderId}` : "N/A",
        productId: order?.productInfo?.productId?._id || "N/A",
        productName: order?.productInfo?.title || "N/A",
        quantity: order?.summary?.quantity || 0,
        buyer: order?.buyerId?.username || "N/A",
        seller: getSellerDisplayName(order),
        salesPrice: order?.summary?.price?.toFixed(2) || "0.00",
        deliveryFee: order?.summary?.deliveryFee?.toFixed(2) || "0.00",
        serviceFee: order?.summary?.serviceFee?.toFixed(2) || "0.00",
        subtotal: order?.summary?.subtotal?.toFixed(2) || "0.00",
        tax: order?.summary?.tax?.toFixed(2) || "0.00",
        totalPaid: order?.summary?.total?.toFixed(2) || "0.00",
        status: order?.currentStatus || "N/A",
        orderDate: new Date(order?.createdAt).toLocaleDateString() || "N/A",
        completionDate: getCompletionDate(order?.statusHistory)?.toLocaleDateString() || "N/A",
        paymentMethod: order?.payment?.method || "N/A",
        buyerName: order?.buyerInfo?.fullName || "N/A",
        buyerEmail: order?.buyerInfo?.email || "N/A",
        buyerCountry: order?.deliveryAddress?.country?.name || "N/A",
        buyerCity: order?.deliveryAddress?.city || "N/A",
        buyerPhone: order?.buyerInfo?.phone || "N/A",
        sellerCompany: order?.sellerId?.companyName || "N/A",
        b2bListingMode: order?.productInfo?.productId?.listingMode || "N/A",
        b2bMoq: order?.productInfo?.productId?.moq || "N/A",
    }));

    const searchFilters = ["Order ID", "Buyer", "Seller"];
    const searchTypeMap = { "Order ID": "ID", "Buyer": "Buyer", "Seller": "Seller" };
    const placeholder = searchType === "ID" ? "Search by Order ID" : searchType === "Buyer" ? "Search by Buyer" : "Search by Seller";

    return (
        <div className='adminOrdersDiv'>
            <div className='adminOrdersContent'>
                <div className="tableDiv">
                    <div className="tableContent">
                        <div className="upper">
                            <h2 className="secondaryHeading">
                                <span>{pre === "dashboard" ? "Recent" : filterType} </span>Orders
                            </h2>
                            <div className="upperRight">
                                <SearchInput
                                    searchType={searchType}
                                    setSearchType={setSearchType}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    searchFilters={searchFilters}
                                    searchTypeMap={searchTypeMap}
                                    placeholder={placeholder}
                                />
                                <CSVLink
                                    data={csvData}
                                    headers={csvHeaders}
                                    filename={`orders_${filterType}.csv`}
                                    className="secondaryBtn"
                                >
                                    Generate CSV
                                </CSVLink>
                                <FaFilter className="filterIcon" onClick={() => setShowFiltersPopup(true)} />
                                {pre === "dashboard" && <Link to="/chwi-admin/orders" className='secondaryBtn'>View All {">"}</Link>}
                            </div>
                        </div>
                        {(filterType !== "All" || startDate || endDate) && <div className="appliedFilters">
                            {filterType !== "All" && (
                                <div className="filterChip">
                                    Status: {filterType}
                                    <IoIosCloseCircleOutline className="closeIcon" onClick={() => setFilterType("All")} />
                                </div>
                            )}
                            {(startDate || endDate) && (
                                <div className="filterChip">
                                    Date: {startDate || "Any"} to {endDate || "Any"}
                                    <IoIosCloseCircleOutline className="closeIcon" onClick={() => { setStartDate(""); setEndDate(""); }} />
                                </div>
                            )}
                            {(filterType !== "All" || startDate || endDate) && (
                                <button className="clearFiltersBtn" onClick={() => { setFilterType("All"); setStartDate(""); setEndDate(""); }}>
                                    Clear All Filters
                                </button>
                            )}
                        </div>}
                        <div className="header">
                            <p className="title">Order Id</p>
                            <p>Buyer</p>
                            <p>Seller</p>
                            <p>Amount</p>
                            <p>Status</p>
                            <p>Order Date</p>
                            <p>Actions</p>
                        </div>
                        <div className="rows">
                            {isLoading ? (
                                <Loader type="simpleMini" />
                            ) : (
                                finalOrders.length > 0 ? finalOrders.map((order, index) => (
                                    <div key={index}>
                                        <div className="requestRow row">
                                            <div className="titleField field">
                                                <p className="title">#{order?.customOrderId}</p>
                                                {order?.isB2B && (
                                                    <span className='adminOrderMarketBadge adminOrderMarketB2B'>B2B</span>
                                                )}
                                            </div>
                                            <p className="buyerField field">{order.buyerId?.username}</p>
                                            {getSellerLinkId(order) ? (
                                                <Link to={`/vendorDetail/${getSellerLinkId(order)}`} className="sellerField field">{getSellerDisplayName(order) + " >"}</Link>
                                            ) : (
                                                <p className="sellerField field">{getSellerDisplayName(order)}</p>
                                            )}
                                            <p className="priceField field">${order?.summary?.total?.toFixed(2)}</p>
                                            <p className="statusField field">{order?.currentStatus}</p>
                                            <p className="dateField field">{new Date(order?.createdAt).toLocaleDateString()}</p>
                                            <div className="actionsField field">
                                                <FaEye className="icon" onClick={() => handleOpenOrderDetails(order)} />
                                            </div>
                                        </div>
                                    </div>
                                )) : <div className="row">Nothing to show here...</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showFiltersPopup && (
                <div className="popupDiv filtersPopup">
                    <div className="popupContent">
                        <div className="filtersForm form">
                            <h3 className='secondaryHeading'>Apply <span>Filters</span></h3>
                            <div className="horizontalLine"></div>
                            <div className="infoText">
                                <FaInfoCircle className="infoIcon2" />
                                <span>Applying start and end date filters will search for orders completed within the specified date range.</span>
                            </div>
                            <div className="filterRow customDateRange">
                                <div>Start Date:</div>
                                <input type="date" value={tempStartDate} onChange={e => setTempStartDate(e.target.value)} max={today} />
                            </div>
                            <div className="filterRow customDateRange">
                                <div>End Date:</div>
                                <input type="date" value={tempEndDate} onChange={e => setTempEndDate(e.target.value)} max={today} />
                            </div>
                            <div className="filterRow">
                                <div>Status:</div>
                                <Dropdown options={productFilters} selected={tempFilterType} onSelect={setTempFilterType} />
                            </div>
                            <div className="buttonsDiv">
                                <button className="secondaryBtn" onClick={() => setShowFiltersPopup(false)}>Close</button>
                                <button className="primaryBtn" onClick={() => {
                                    setFilterType(tempFilterType);
                                    setStartDate(tempStartDate);
                                    setEndDate(tempEndDate);
                                    setShowFiltersPopup(false);
                                }}>Apply Filters</button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowFiltersPopup(false)} />
                    </div>
                </div>
            )}

            {showOrderDetailsModel && (
                <div className="popupDiv addNewModelDiv">
                    <div className="popupContent">
                        <div className='form'>
                            <h2 className="secondaryHeading">About <span>Order</span></h2>
                            <div className="rows">
                                <div className="row">
                                    <div>Product Category</div>
                                    <div className="fw600">{openedOrder?.productInfo?.productId?.maincategory || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Product SubCategory</div>
                                    <div className="fw600">{openedOrder?.productInfo?.productId?.subcategory || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Order Completion Date</div>
                                    <div className="fw600">{getCompletionDate(openedOrder?.statusHistory)?.toLocaleDateString() || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Quantity</div>
                                    <div className="fw600">{openedOrder?.summary?.quantity || 0}</div>
                                </div>
                            </div>

                            {openedOrder?.isB2B && (
                                <>
                                    <div className="horizontalLine"></div>
                                    <h2 className="secondaryHeading">B2B <span>Details</span></h2>
                                    <div className="rows">
                                        <div className="row">
                                            <div>Company</div>
                                            <div className="fw600">{openedOrder?.sellerId?.companyName || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Organization</div>
                                            <div className="fw600">{openedOrder?.sellerId?.natureOfOrg || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Employee Range</div>
                                            <div className="fw600">{openedOrder?.sellerId?.numberOfEmployees || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Listing Mode</div>
                                            <div className="fw600">{openedOrder?.productInfo?.productId?.listingMode || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>Listing Type</div>
                                            <div className="fw600">{openedOrder?.productInfo?.productId?.listingType || "N/A"}</div>
                                        </div>
                                        <div className="row">
                                            <div>MOQ</div>
                                            <div className="fw600">{openedOrder?.productInfo?.productId?.moq || "N/A"}</div>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">Paid by <span>Buyer</span></h2>
                            <div className="rows">
                                <div className="row">
                                    <div>Payment Method</div>
                                    <div className="fw600">{openedOrder?.payment?.method ? openedOrder.payment.method.charAt(0).toUpperCase() + openedOrder.payment.method.slice(1) : "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Sales Price</div>
                                    <div className="fw600">${openedOrder?.summary?.price?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Delivery Fee</div>
                                    <div className="fw600">${openedOrder?.summary?.deliveryFee?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Service Fee</div>
                                    <div className="fw600">${openedOrder?.summary?.serviceFee?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Subtotal</div>
                                    <div className="fw600">${openedOrder?.summary?.subtotal?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Tax</div>
                                    <div className="fw600">${openedOrder?.summary?.tax?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Buyer Paid</div>
                                    <div className="fw600">${openedOrder?.summary?.total?.toFixed(2) || "0.00"}</div>
                                </div>
                            </div>

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">Seller <span>Earning</span></h2>
                            <div className="rows">
                                <div className="row">
                                    <div>Sales Price</div>
                                    <div className="fw600">${openedOrder?.summary?.price?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Delivery Fee</div>
                                    <div className="fw600">${openedOrder?.summary?.deliveryFee?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Service Fee</div>
                                    <div className="fw600">-${openedOrder?.summary?.serviceFee?.toFixed(2) || "0.00"}</div>
                                </div>
                                <div className="row">
                                    <div>Seller To Get</div>
                                    <div className="fw600">${((openedOrder?.summary?.subtotal || 0) - (openedOrder?.summary?.serviceFee || 0)).toFixed(2)}</div>
                                </div>
                            </div>

                            <div className="horizontalLine"></div>

                            <h2 className="secondaryHeading">About <span>Buyer</span></h2>
                            <div className='rows'>
                                <div className="row">
                                    <div>Name</div>
                                    <div className="fw600">{openedOrder?.buyerInfo?.fullName || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Email</div>
                                    <div className="fw600">{openedOrder?.buyerInfo?.email || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Country</div>
                                    <div className="fw600">{openedOrder?.deliveryAddress?.country?.name || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>City</div>
                                    <div className="fw600">{openedOrder?.deliveryAddress?.city || "N/A"}</div>
                                </div>
                                <div className="row">
                                    <div>Phone</div>
                                    <div className="fw600">{openedOrder?.buyerInfo?.phone || "N/A"}</div>
                                </div>
                            </div>
                            <div className="contactBuyerBtnDiv">
                                <Link to={`/chwi-admin/chats/?p=${openedOrder?.buyerId?._id}`}>Contact Buyer {" >"}</Link>
                            </div>

                            <div className="buttonsDiv">
                                <button className="secondaryBtn" type="button" onClick={() => setShowOrderDetailsModel(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowOrderDetailsModel(false)} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminOrders;